/**
 * Privacy notice for joltstream.
 *
 * Required under UK GDPR Articles 13/14 because the service processes
 * personal data (transient IPs at the edge for rate limiting, location
 * data that could in principle be aggregated to identify routes).
 *
 * The substantive privacy posture lives in About — "what we don't do"
 * is the more interesting half of the story. This page repeats those
 * commitments in a form that satisfies the statutory disclosure
 * requirements (controller identity, lawful basis, rights, contact)
 * and provides a route for data-subject requests even though there's
 * generally nothing to act on (no PII to retrieve or erase).
 *
 * Update both `LAST_UPDATED` and the version history below when
 * substantive changes land.
 */
const LAST_UPDATED = '2026-05-18';

export function PrivacyPage() {
  return (
    <div className="page prose">
      <h1>Privacy notice</h1>
      <p className="muted">
        Last updated: {LAST_UPDATED}
      </p>

      <h2>Who we are</h2>
      <p>
        joltstream is operated by <strong>Time Back Solutions Ltd</strong>,
        a company registered in England and Wales. We are the data
        controller for any personal data processed by this service.
      </p>
      <p>
        Information Commissioner's Office (ICO) registration:{' '}
        <strong>application pending</strong>, reference{' '}
        <strong>C1935359</strong>. The annual data-protection fee
        has been set up by direct debit; the registration will be
        listed on the public ICO register once ICO process the
        first payment. We update this notice when that lands.
      </p>

      <h2>What we collect</h2>
      <p>
        Each row below is a piece of information the service handles,
        with what it's used for, where it's kept, and how long.
      </p>
      <table className="data-inventory">
        <thead>
          <tr>
            <th>Data</th>
            <th>Purpose</th>
            <th>Where stored</th>
            <th>Retention</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Location of each detected jolt</td>
            <td>Plot the event on the public map</td>
            <td>Our database</td>
            <td>Long-lived (open-data archive)</td>
          </tr>
          <tr>
            <td>Vehicle speed at the moment of the jolt</td>
            <td>Helps distinguish real road events from random noise</td>
            <td>Our database</td>
            <td>Long-lived</td>
          </tr>
          <tr>
            <td>Strength of the jolt</td>
            <td>Tells potholes apart from speed bumps and lane changes</td>
            <td>Our database</td>
            <td>Long-lived</td>
          </tr>
          <tr>
            <td>A confidence rating for the detection</td>
            <td>Lets the map filter out low-quality readings</td>
            <td>Our database</td>
            <td>Long-lived</td>
          </tr>
          <tr>
            <td>A coarse device category</td>
            <td>A broad calibration bucket (e.g. "modern iPhone"). Not a model number, not a serial, not anything that picks out a specific device.</td>
            <td>Our database</td>
            <td>Long-lived</td>
          </tr>
          <tr>
            <td>A short-lived group label</td>
            <td>Lets the database keep events from the same few seconds together. The label resets after a brief pause and never carries across trips.</td>
            <td>Our database</td>
            <td>Long-lived</td>
          </tr>
          <tr>
            <td>Time offset within that group</td>
            <td>Allows ordering of jolts within those few seconds. No absolute time is recorded.</td>
            <td>Our database</td>
            <td>Long-lived</td>
          </tr>
          <tr>
            <td>Your IP address</td>
            <td>Used briefly at the edge to block abuse. Never saved alongside any jolt.</td>
            <td>Cloudflare + edge logs only</td>
            <td>Transient (typically days)</td>
          </tr>
          <tr>
            <td>Whether you've granted location and motion access</td>
            <td>Avoids re-asking every visit</td>
            <td>On your device</td>
            <td>Until you clear it</td>
          </tr>
          <tr>
            <td>A local list of the jolts your device produced</td>
            <td>Lets the public map always show you your own jolts</td>
            <td>On your device</td>
            <td>Until you clear it</td>
          </tr>
          <tr>
            <td>An offline send-later queue</td>
            <td>Holds jolts you captured without signal until your device is online</td>
            <td>On your device</td>
            <td>Removed once uploaded</td>
          </tr>
          <tr>
            <td>App files cached for offline use</td>
            <td>Faster loads and lets the app work without signal</td>
            <td>On your device</td>
            <td>Refreshed when we ship an update</td>
          </tr>
        </tbody>
      </table>

      <p>What we do <em>not</em> collect, send, store, or derive:</p>
      <ul>
        <li>No absolute date or time of any jolt. We can't tell what time of day or day of week a jolt happened.</li>
        <li>No account, no login, no advertising identifier.</li>
        <li>No raw trip track. Jolts within roughly 75 m of where you start and where you stop a journey are not sent. This is so the map can't be used to infer home or work.</li>
        <li>No per-device or per-person identifier. Nothing we keep can be used to pick a single device out of a crowd.</li>
        <li>No IP address kept alongside a jolt. IP is only used briefly at the edge to filter abuse.</li>
        <li>No raw motion stream. The high-rate sensor data stays on your device — only the handful of detected jolts ever leave it.</li>
      </ul>

      <h2>Lawful basis</h2>
      <p>
        We process the data above on the lawful basis of{' '}
        <strong>legitimate interest</strong> (UK GDPR Article 6(1)(f)).
        Our legitimate interest is building anonymous open data about
        UK road surface quality for public benefit. The data we
        collect is the minimum required for that purpose, with a
        privacy posture designed to make individual identification
        infeasible.
      </p>

      <h2>How long we keep it</h2>
      <p>
        Event records are intended to be long-lived — the open-data
        value increases with history. Because none of those records
        identifies you, "keeping them" doesn't keep anything about
        you specifically. There is, by design, nothing to delete
        about a specific person.
      </p>
      <p>
        Transient data (server access logs, Cloudflare logs) is
        retained per our infrastructure providers' standard retention
        — typically a few days to a few weeks for operational
        diagnostics and abuse prevention.
      </p>

      <h2>Who we share it with</h2>
      <p>
        Aggregated event data will be published openly under an open
        data licence (ODbL or CC-BY 4.0; final choice when the
        dataset reaches useful coverage). Anyone will then be able
        to download or query the public dataset.
      </p>
      <p>The infrastructure providers we use:</p>
      <ul>
        <li><strong>Cloudflare</strong> — content delivery network, TLS termination, DDoS protection.</li>
        <li><strong>Our own server infrastructure</strong> — UK-based, operated by Time Back Solutions Ltd.</li>
      </ul>
      <p>
        We do not sell, rent, or otherwise hand event data to advertisers
        or trackers, and the data we collect is not in a form that would
        be useful to them anyway.
      </p>

      <h2>What stays on your device</h2>
      <p>
        joltstream keeps a small amount of local state on your device.
        Nothing in it leaves your device beyond what's listed above.
      </p>
      <ul>
        <li>
          <strong>Permission status</strong> — remembers that you've
          granted location and motion access so we don't ask every visit.
        </li>
        <li>
          <strong>Your own jolts list</strong> — so the map can always
          show you your own jolts even when the viewer-comfort filter
          would otherwise hide them.
        </li>
        <li>
          <strong>Offline send-later queue</strong> — jolts detected
          without signal are kept on your device and uploaded when you
          reconnect.
        </li>
        <li>
          <strong>App files cache</strong> — the app itself, kept for
          offline use and faster loads.
        </li>
      </ul>
      <p>
        All four are needed for the service to work. We do not use any
        of them for tracking, analytics, or advertising.
      </p>

      <h2>Cookies</h2>
      <p>
        joltstream itself does not set cookies. The small amount of
        local state above is kept in browser storage, not in cookies,
        and is never used for tracking, analytics, or advertising.
      </p>
      <p>
        Two narrowly-scoped cookies may be set by our infrastructure:
      </p>
      <ul>
        <li>
          <strong>Cloudflare</strong> — sets a short-lived bot-detection
          cookie, and may set a clearance cookie when a visitor passes a
          security challenge. These are needed for Cloudflare to
          distinguish real users from abusive automated traffic.
        </li>
        <li>
          <strong>Admin sign-in</strong> — set only when an administrator
          signs in to a protected admin page. Not set for ordinary
          visitors.
        </li>
      </ul>
      <p>
        Both qualify as <em>strictly necessary</em> under the Privacy
        and Electronic Communications Regulations (PECR), because the
        service cannot operate without them. We therefore don't show a
        cookie consent banner. If you'd rather not have any of these
        cookies set, the only way to avoid them is to not visit the
        site — there is no in-app toggle because there is no in-app
        code that sets them.
      </p>

      <h2>Your rights</h2>
      <p>
        Under UK GDPR you have the right to:
      </p>
      <ul>
        <li>Ask what personal data we hold about you (subject access request)</li>
        <li>Ask us to correct or erase data we hold about you</li>
        <li>Object to or restrict our processing</li>
        <li>Receive your data in a portable format</li>
        <li>Complain to the ICO if you think we're getting it wrong</li>
      </ul>
      <p>
        In practice, because we hold no per-person identifier, we
        cannot locate "your" data even if you ask us to — there is
        no key to look it up by. A subject access request will
        therefore truthfully return "no records found for you".
        Erasure requests will similarly truthfully return "nothing
        to erase". This is by design, not negligence.
      </p>

      <h2>Children</h2>
      <p>
        joltstream is not directed at children under 13. We do not
        knowingly collect personal data from children.
      </p>

      <h2>Contact</h2>
      <p>
        For any data-protection question, request, or complaint:
      </p>
      <ul>
        <li>
          Email: <a href="mailto:privacy@joltstream.app">privacy@joltstream.app</a>
        </li>
      </ul>
      <p>
        You may also complain directly to the ICO at{' '}
        <a href="https://ico.org.uk/make-a-complaint/">ico.org.uk/make-a-complaint</a>{' '}
        or by phoning 0303 123 1113.
      </p>

      <h2>Changes to this notice</h2>
      <p>
        If we change anything substantive about how the service handles
        data, we will update this page and the "last updated" date at
        the top. Past versions of this notice are available on request —
        write to <a href="mailto:privacy@joltstream.app">privacy@joltstream.app</a>.
      </p>
    </div>
  );
}
